import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"

import type {
  DraftAdjustment,
  DraftForecast,
  SalesLevel,
} from "~/components/demand-2/demandDraftState"
import { sameStringSet, useNoosEnabledDraft } from "~/components/demand-2/demandDraftState"
import { type NoosMetricKey } from "~/components/demand-2/shared/noosBreakdown"
import { CELL_DELTA_THEME, MANUAL_ADJUSTMENT_NAME } from "~/components/demand-2/shared/cellDelta"
import { useAsOfEditStart } from "~/utils/editSession"
import { alwaysOnInsightKeys, resolveInsightSlots } from "~/utils/demandInsightValues"
import type { InsightNode, NodeInsightRow } from "~/utils/demandInsightNodes"
import {
  effectiveAnswerAt,
  effectiveAnswerFor,
  type FramingAnswerRow,
  type FramingAnswers,
  type FramingNode,
  type FramingQuestionId,
  NO_FRAMING_ANSWERS,
  VARIANT_ANSWER_QUESTIONS,
  WHOLE_COLLECTION_NODE,
} from "~/utils/demandFraming"
import { parseSeasonTrendMode, type SeasonTrendMode } from "~/utils/demandSeasonTrendMode"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useDemandScope } from "~/components/demand-2/useDemandScope"
import { api, type RouterOutputs } from "~/utils/api"
import {
  readbackOf,
  useInvalidateDemandReads,
  useTotalsReadbackSlices,
  type TotalsReadback,
  type TotalsReadbackSlice,
} from "~/utils/cacheUtils/demandCacheHelpers"
import {
  CHANNEL_ALL,
  isCollectionWide,
  MARKET_ALL,
  type ScopeColumns,
  scopeToColumns,
  WAREHOUSE_ALL,
  WHOLE_VARIANT_SCOPE,
} from "~/utils/demandScope"
import { showErrorToast } from "~/utils/toaster"
import { readStrategyFilters } from "~/utils/demandStrategyScope"

const noop = () => {}

/**
 * Per-variant mutations all answered at the same slices, so rows concatenate per slice. Null if any
 * carried nothing, so a partial answer is never spliced over the rest.
 */
const mergeReadback = (
  sent: TotalsReadbackSlice[],
  results: ({ totals?: TotalsReadback | null } | null)[],
): { sent: TotalsReadbackSlice[]; rows: TotalsReadback } | null => {
  if (sent.length === 0 || results.length === 0) return null
  const carried = results.flatMap((r) => (r?.totals ? [r.totals] : []))
  if (carried.length !== results.length) return null
  return { sent, rows: sent.map((_slice, i) => carried.flatMap((t) => t[i] ?? [])) }
}

// Composite scope columns ("" = "all") <-> single-axis SalesLevel (most-specific axis).
export function rowToLevel(row: ScopeColumns): SalesLevel {
  if (row.marketId !== MARKET_ALL) return { kind: "market", id: row.marketId }
  if (row.warehouseId !== WAREHOUSE_ALL) return { kind: "warehouse", id: row.warehouseId }
  if (row.channel !== CHANNEL_ALL) return { kind: "channel", id: row.channel }
  return { kind: "total" }
}

function levelToScopeArgs(level: SalesLevel): ScopeColumns {
  switch (level.kind) {
    case "channel":
      return scopeToColumns({ channel: level.id, warehouseId: null, marketId: null })
    case "warehouse":
      return scopeToColumns({ channel: null, warehouseId: level.id, marketId: null })
    case "market":
      return scopeToColumns({ channel: null, warehouseId: null, marketId: level.id })
    default:
      // total + customer (out of v1) → all
      return scopeToColumns(WHOLE_VARIANT_SCOPE)
  }
}

type DemandEditState = RouterOutputs["demand2"]["getDemandEditState"]

/**
 * One merged read behind every edit list, so a page load costs one workspace resolve, not six.
 * Returns only the slice, a stable cache reference, so downstream memos don't churn.
 */
export const useEditStateSlice = <K extends keyof DemandEditState>(
  key: K,
  collectionIds: number[],
  enabled: boolean,
): { data: DemandEditState[K] | undefined; isLoading: boolean } => {
  const q = api.demand2.getDemandEditState.useQuery(
    { collectionIds },
    { enabled: enabled && collectionIds.length > 0 },
  )
  return { data: q.data?.[key], isLoading: q.isLoading }
}

/** The merged read itself, for surfaces that hold their own `collectionIds` rather than the route scope. */
export const useDemandEditState = (collectionIds: number[], enabled = true) =>
  api.demand2.getDemandEditState.useQuery(
    { collectionIds },
    { enabled: enabled && collectionIds.length > 0 },
  )

/** Each variant's whole-variant pin (all channels, markets, warehouses), by variant. */
export const useWholeVariantPins = (collectionIds: number[], enabled = true) => {
  const { data } = useDemandEditState(collectionIds, enabled)
  return useMemo(() => {
    const m = new Map<string, number>()
    for (const o of data?.overrides ?? []) {
      if (o.channel === CHANNEL_ALL && o.warehouseId === WAREHOUSE_ALL && o.marketId === MARKET_ALL)
        m.set(o.variantId, o.total)
    }
    return m
  }, [data])
}

/**
 * Collection-wide adjustments to prepend to a variant's own stack, in engine order; rows sharing a
 * groupId collapse to one. The client has no variant→collection map, so only groups with a row in
 * every in-scope collection are returned.
 */
export function useAttributableCollectionWide(): DraftAdjustment[] {
  const { collectionIds } = useDemand2Scope()
  const [all] = useDemandAdjustments()
  return useMemo(() => attributable(all, collectionIds.length), [all, collectionIds.length])
}

function attributable(all: DraftAdjustment[], scopeSize: number): DraftAdjustment[] {
  const byGroup = new Map<string, DraftAdjustment[]>()
  for (const a of all) {
    if (!isCollectionWide(a)) continue
    const key = a.groupId ?? a.id
    const rows = byGroup.get(key)
    if (rows) rows.push(a)
    else byGroup.set(key, [a])
  }
  return [...byGroup.values()].filter((r) => r.length >= scopeSize).map((r) => r[0]!)
}

const toDraftAdjustment = (r: DemandEditState["adjustments"][number]): DraftAdjustment => ({
  id: r.id,
  groupId: r.groupId,
  variantId: r.variantId,
  level: rowToLevel(r),
  scope: { channel: r.channel, warehouseId: r.warehouseId, marketId: r.marketId },
  stage:
    r.stage === "setup" || r.stage === "manual"
      ? (r.stage as "setup" | "manual")
      : ("overlay" as const),
  target: r.target === "preorder" || r.target === "both" ? r.target : ("sales" as const),
  theme: r.theme ?? "",
  name: r.name,
  description: r.description,
  params: r.params,
  filters: readStrategyFilters(r.filters),
  type: r.type === "percentage" ? "percentage" : "add",
  value: r.value,
  startDate: r.startDate ?? null,
  endDate: r.endDate ?? null,
  seq: r.seq,
  ts: new Date(r.createdAt).getTime(),
  createdBy: r.createdBy,
})

export function useDemandAdjustments(): [DraftAdjustment[], (next: DraftAdjustment[]) => void] {
  const invalidate = useInvalidateDemandReads(
    "adjustment_add",
    "adjustment_edit",
    "adjustment_delete",
  )
  const slicesFor = useTotalsReadbackSlices()
  const { collectionIds } = useDemand2Scope()
  const enabled = collectionIds.length > 0
  const q = useEditStateSlice("adjustments", collectionIds, enabled)
  // Stable `mutateAsync`, not the useMutation() object (new ref each render), or every callback
  // closing over it churns and consumers' grids force-refresh.
  const { mutateAsync: saveAdjustment } = api.demand2.saveDemandAdjustment.useMutation()
  const { mutateAsync: removeAdjustment } = api.demand2.deleteDemandAdjustment.useMutation()

  const value = useMemo(() => (q.data ?? []).map(toDraftAdjustment), [q.data])

  const setValue = useCallback(
    (next: DraftAdjustment[]) => {
      const currentIds = new Set(value.map((a) => a.id))
      const nextIds = new Set(next.map((a) => a.id))
      const added = next.filter((a) => !currentIds.has(a.id))
      const removed = value.filter((a) => !nextIds.has(a.id))
      const touched = [...added, ...removed].map((a) => a.variantId)
      const sent = slicesFor(touched)
      void Promise.all([
        ...added.map((a) =>
          saveAdjustment({
            collectionIds,
            variantIds: [a.variantId],
            ...levelToScopeArgs(a.level),
            stage: a.stage ?? "overlay",
            target: a.target ?? "sales",
            type: a.type,
            value: a.value,
            name: a.name,
            theme: a.theme,
            readback: sent,
          }),
        ),
        ...removed.map((a) => removeAdjustment({ collectionIds, id: a.id, readback: sent })),
      ])
        .then(
          (results) => invalidate(touched, mergeReadback(sent, results)),
          (err) => {
            showErrorToast(err)
            return invalidate(touched)
          },
        )
        .catch(noop)
    },
    [value, collectionIds, saveAdjustment, removeAdjustment, invalidate, slicesFor],
  )

  return [value, setValue]
}

// `value` is frozen at accept and sized by the server.
type AcceptedInsight = { insightKey: string; value: number }

type SetupInsightEntry = {
  variantId: string
  insightKeys: string[]
}

// Accepted insights for the active scope, in their OWN layer — never in the adjustments table.
export function useDemandSetupInsights(): {
  byVariant: Map<string, Map<string, AcceptedInsight>>
  setForVariants: (
    entries: SetupInsightEntry[],
    readback?: TotalsReadbackSlice[],
  ) => Promise<{
    totals?: TotalsReadback | null
    skipped?: { variantId: string; insightKey: string }[]
  } | null>
  invalidate: ReturnType<typeof useInvalidateDemandReads>
} {
  const invalidate = useInvalidateDemandReads("insight_set")
  const { collectionIds } = useDemand2Scope()
  const enabled = collectionIds.length > 0
  const q = useEditStateSlice("setupInsights", collectionIds, enabled)
  const { mutateAsync: saveSetupInsights } = api.demand2.setDemandSetupInsightsBulk.useMutation()

  const byVariant = useMemo(() => {
    const m = new Map<string, Map<string, AcceptedInsight>>()
    for (const r of q.data ?? []) {
      let s = m.get(r.variantId)
      if (!s) {
        s = new Map()
        m.set(r.variantId, s)
      }
      s.set(r.insightKey, { insightKey: r.insightKey, value: r.value })
    }
    return m
  }, [q.data])

  const setForVariants = useCallback(
    (entries: SetupInsightEntry[], readback?: TotalsReadbackSlice[]) =>
      entries.length === 0
        ? Promise.resolve(null)
        : saveSetupInsights({ collectionIds, entries, readback }),
    [collectionIds, saveSetupInsights],
  )

  return { byVariant, setForVariants, invalidate }
}

/**
 * NOOS opt-in insight toggles. Edits live in the page-wide session draft so the Commandbar can
 * batch Apply/Discard; Apply replaces each changed variant's key set, keeping frozen values.
 */
export function useNoosInclusions(collectionId: number) {
  const { byVariant, setForVariants, invalidate } = useDemandSetupInsights()
  const slicesFor = useTotalsReadbackSlices()
  const [draft, setDraft] = useNoosEnabledDraft(collectionId)

  const appliedFor = useCallback(
    (variantId: string): ReadonlySet<string> => new Set(byVariant.get(variantId)?.keys() ?? []),
    [byVariant],
  )
  // Applied (frozen) insight values, already folded into the forecast.
  const appliedSumFor = useCallback(
    (variantId: string): number => {
      let s = 0
      for (const ins of byVariant.get(variantId)?.values() ?? []) s += ins.value
      return s
    },
    [byVariant],
  )
  const enabledFor = useCallback(
    (variantId: string): ReadonlySet<string> => new Set(byVariant.get(variantId)?.keys() ?? []),
    [byVariant],
  )
  const selectedFor = useCallback(
    (variantId: string): ReadonlySet<string> =>
      variantId in draft ? new Set(draft[variantId]) : enabledFor(variantId),
    [draft, enabledFor],
  )
  const hasUnsaved = useCallback(
    (variantId: string): boolean =>
      variantId in draft && !sameStringSet(draft[variantId], [...enabledFor(variantId)]),
    [draft, enabledFor],
  )
  const changedVariantIds = useMemo(
    () => Object.keys(draft).filter((id) => !sameStringSet(draft[id], [...enabledFor(id)])),
    [draft, enabledFor],
  )

  const toggle = useCallback(
    (variantId: string, key: NoosMetricKey, included: boolean) => {
      const next = new Set(selectedFor(variantId))
      if (included) next.add(key)
      else next.delete(key)
      setDraft({ ...draft, [variantId]: [...next] })
    },
    [selectedFor, draft, setDraft],
  )
  // Commandbar bulk accept. Callers pass only variants that offer the key.
  const toggleMany = useCallback(
    (variantIds: string[], key: NoosMetricKey, included: boolean) => {
      const next = { ...draft }
      for (const variantId of variantIds) {
        const keys = new Set(selectedFor(variantId))
        if (included) keys.add(key)
        else keys.delete(key)
        next[variantId] = [...keys]
      }
      setDraft(next)
    },
    [selectedFor, draft, setDraft],
  )
  const revertVariant = useCallback(
    (variantId: string) => {
      const rest = { ...draft }
      delete rest[variantId]
      setDraft(rest)
    },
    [draft, setDraft],
  )
  const discard = useCallback(() => setDraft({}), [setDraft])

  const apply = useCallback(() => {
    // One request for the batch: select-all spans thousands of variants. Keys only: each entry
    // replaces the variant's whole set and the server sizes the frozen value, which the page can't.
    const touchedVariantIds = Object.keys(draft)
    const entries = touchedVariantIds.map((variantId) => {
      return { variantId, insightKeys: [...new Set(draft[variantId])] }
    })
    const sent = slicesFor(touchedVariantIds)
    void setForVariants(entries, sent)
      .then(
        (r) => {
          setDraft({}) // clear staged toggles only after the writes land
          // A key the engine offers nothing for stores no row; warn, don't spring back silently.
          if (r?.skipped?.length) {
            const keys = [...new Set(r.skipped.map((s) => s.insightKey))].join(", ")
            const variants = new Set(r.skipped.map((s) => s.variantId)).size
            toast.warning(
              `Nothing to apply for ${keys} on ${variants} ${variants === 1 ? "variant" : "variants"}.`,
            )
          }
          return invalidate(touchedVariantIds, readbackOf(sent, r))
        },
        // Keep the draft on failure so staged toggles aren't silently lost.
        (err) => {
          showErrorToast(err)
          return invalidate(touchedVariantIds)
        },
      )
      .catch(noop)
  }, [draft, setForVariants, invalidate, setDraft, slicesFor])

  return useMemo(
    () => ({
      appliedFor,
      appliedSumFor,
      selectedFor,
      hasUnsaved,
      changedVariantIds,
      count: changedVariantIds.length,
      toggle,
      toggleMany,
      revertVariant,
      apply,
      discard,
    }),
    [
      appliedFor,
      appliedSumFor,
      selectedFor,
      hasUnsaved,
      changedVariantIds,
      toggle,
      toggleMany,
      revertVariant,
      apply,
      discard,
    ],
  )
}

/**
 * Collection-wide insight keys, written straight through (one row per key, nothing to batch). The
 * engine sizes each variant from them at read time; an individually accepted insight stays frozen.
 */
export function useCollectionInsights(): {
  /** Stored keys plus the kind's always-on set. */
  enabled: ReadonlySet<NoosMetricKey>
  isLoading: boolean
  setEnabled: (key: NoosMetricKey, on: boolean) => void
  /** Whether the switches were ever settled. An empty set alone cannot say. */
  reviewed: boolean
  setReviewed: () => void
} {
  const utils = api.useUtils()
  const invalidate = useInvalidateDemandReads("insight_set", "insights_reviewed_set")
  const { collectionIds, kind } = useDemand2Scope()
  const enabled = collectionIds.length > 0
  const q = useEditStateSlice("collectionInsights", collectionIds, enabled)
  const reviewedQ = useEditStateSlice("insightsReviewed", collectionIds, enabled)
  const { mutateAsync: save } = api.demand2.setDemandCollectionInsights.useMutation()
  const { mutateAsync: saveReviewed } = api.demand2.setDemandInsightsReviewed.useMutation()

  // Must mirror `expandCollectionInsights`, or Settings states a total the plan doesn't carry.
  const alwaysOn = alwaysOnInsightKeys(kind ?? "noos")
  const keys = useMemo(
    () => new Set([...(q.data ?? []).map((r) => r.insightKey as NoosMetricKey), ...alwaysOn]),
    [q.data, alwaysOn],
  )

  const setEnabled = useCallback(
    (key: NoosMetricKey, on: boolean) => {
      const next = new Set(keys)
      if (on) next.add(key)
      else next.delete(key)
      // Resolved client-side too, so the optimistic write already shows the rival switch off.
      const insightKeys = resolveInsightSlots(next, keys)
      // No-op when nothing is cached: a partial object would hand other consumers an empty list.
      utils.demand2.getDemandEditState.setData({ collectionIds }, (prev) =>
        prev
          ? { ...prev, collectionInsights: insightKeys.map((insightKey) => ({ insightKey })) }
          : prev,
      )
      void save({ collectionIds, insightKeys })
        .catch(showErrorToast)
        // Full invalidation: every variant moves. Also corrects the optimistic write on failure.
        .finally(() => void invalidate())
    },
    [keys, collectionIds, save, invalidate, utils],
  )

  const setReviewed = useCallback(() => {
    // No plan numbers move, so only the edit-state read is refreshed, not every demand read.
    utils.demand2.getDemandEditState.setData({ collectionIds }, (prev) =>
      prev ? { ...prev, insightsReviewed: true } : prev,
    )
    void saveReviewed({ collectionIds, reviewed: true })
      .catch(showErrorToast)
      .finally(() => void utils.demand2.getDemandEditState.invalidate({ collectionIds }))
  }, [collectionIds, saveReviewed, utils])

  return useMemo(
    () => ({
      enabled: keys,
      isLoading: q.isLoading,
      setEnabled,
      reviewed: reviewedQ.data ?? false,
      setReviewed,
    }),
    [keys, q.isLoading, setEnabled, reviewedQ.data, setReviewed],
  )
}

const NO_NODE_ROWS: readonly NodeInsightRow[] = []

/**
 * Per-node overrides of the collection insight setting, one row per deviating node; writes
 * straight through. `setNode(…, null)` clears the node's answer. Enabling a key silences its
 * rivals server-side; the optimistic patch moves only the touched row, the invalidation the rest.
 */
export function useNodeInsights(): {
  rows: readonly NodeInsightRow[]
  isLoading: boolean
  setNode: (node: InsightNode, key: NoosMetricKey, enabled: boolean | null) => void
} {
  const utils = api.useUtils()
  const invalidate = useInvalidateDemandReads("node_insight_set")
  const { collectionIds } = useDemand2Scope()
  const enabled = collectionIds.length > 0
  const q = useEditStateSlice("nodeInsights", collectionIds, enabled)
  const { mutateAsync: save } = api.demand2.setDemandNodeInsight.useMutation()

  const setNode = useCallback(
    (node: InsightNode, key: NoosMetricKey, next: boolean | null) => {
      utils.demand2.getDemandEditState.setData({ collectionIds }, (prev) => {
        if (!prev) return prev
        const kept = prev.nodeInsights.filter(
          (r) =>
            !(
              r.groupKey === node.groupKey &&
              r.groupValue === node.groupValue &&
              r.insightKey === key
            ),
        )
        return {
          ...prev,
          nodeInsights:
            next == null ? kept : [...kept, { ...node, insightKey: key, enabled: next }],
        }
      })
      void save({ collectionIds, ...node, insightKey: key, enabled: next })
        .catch(showErrorToast)
        // Full invalidation: every variant under the node moves. Also undoes a failed write.
        .finally(() => void invalidate())
    },
    [collectionIds, save, invalidate, utils],
  )

  return useMemo(
    () => ({ rows: q.data ?? NO_NODE_ROWS, isLoading: q.isLoading, setNode }),
    [q.data, q.isLoading, setNode],
  )
}

/**
 * Weekly gross-realised sales over the level trend's two 364-day windows, for display only. The
 * breakdown card unmounts while collapsed, which keeps this scan off page load.
 */
export function useLevelSalesWeekly({ enabled = true }: { enabled?: boolean } = {}): {
  weeks: { week: string; quantity: number }[]
  isLoading: boolean
} {
  const { collectionIds } = useDemand2Scope()
  const q = api.demand2.getDemandLevelSalesWeekly.useQuery(
    { collectionIds },
    { enabled: enabled && collectionIds.length > 0 },
  )
  return { weeks: q.data ?? EMPTY_WEEKS, isLoading: q.isLoading }
}

const EMPTY_WEEKS: { week: string; quantity: number }[] = []

/**
 * One side of a fashion lineage: a whole collection group at group grain, or the linked collections
 * at collection grain.
 */
export type SeasonSide = {
  collectionIds: number[]
  grain: "collection" | "group"
  names: string[]
  start: string | null
  end: string | null
  finished: boolean
  /** Collections on this side still open at `asOf`. */
  running: { name: string; end: string | null }[]
}

/**
 * Growth from the comparable lineage (baseline season vs the one before). `unavailable` names which
 * decision is missing: each has a different fix.
 */
export function useSeasonSalesTrend({ enabled = true }: { enabled?: boolean } = {}): {
  pct: number | null
  year1: number
  year2: number
  comparable: SeasonSide | null
  prior: SeasonSide | null
  unavailable: "noComparable" | "noPrior" | "unfinished" | null
  /** See `seasonTrendApplicable`. */
  applicable: boolean
  nodes: NodeSalesTrend[]
  levelKeys: string[]
  isLoading: boolean
} {
  const { collectionIds } = useDemand2Scope()
  const q = api.demand2.getDemandSeasonSalesTrend.useQuery(
    { collectionIds },
    { enabled: enabled && collectionIds.length > 0 },
  )
  return useMemo(
    () => ({
      pct: q.data?.pct ?? null,
      year1: q.data?.year1 ?? 0,
      year2: q.data?.year2 ?? 0,
      comparable: q.data?.window.comparable ?? null,
      prior: q.data?.window.prior ?? null,
      // Loading reads as unavailable, so the row never flashes a figure-less switch.
      unavailable: q.data?.lineage.unavailable ?? "noComparable",
      applicable: q.data?.applicable ?? false,
      nodes: q.data?.nodes ?? EMPTY_NODES,
      levelKeys: q.data?.levelKeys ?? EMPTY_LEVELS,
      isLoading: q.isLoading,
    }),
    [q.data, q.isLoading],
  )
}

const EMPTY_ANSWERS: FramingAnswerRow[] = []

/**
 * Questions this scope can answer: the Insights card's (`questions`) and the Strategy step's growth
 * question (`strategyQuestions`, empty on NOOS).
 */
export function useAdjustmentQuestions():
  | { questions: readonly FramingQuestionId[]; strategyQuestions: readonly FramingQuestionId[] }
  | undefined {
  const { collectionIds } = useDemand2Scope()
  const q = api.demand2.getDemandAdjustmentQuestions.useQuery(
    { collectionIds },
    { enabled: collectionIds.length > 0 },
  )
  // `undefined` while in flight, never empty lists, or a caller counting against it states 0 of 0.
  return q.data
}

/** Node, per-variant and slice answers on the workspace, for readers of `effectiveAnswerFor`. */
export function useFraming(): FramingAnswers {
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0]
  const q = api.demand2.getDemandFraming.useQuery(
    { collectionId: collectionId ?? 0 },
    { enabled: collectionId != null },
  )
  return useMemo(
    () =>
      q.data
        ? {
            answers: q.data.answers,
            variantAnswers: q.data.variantAnswers.filter((a) =>
              VARIANT_ANSWER_QUESTIONS.includes(a.question),
            ),
            sliceAnswers: q.data.sliceAnswers,
          }
        : NO_FRAMING_ANSWERS,
    [q.data],
  )
}

export function useSizeSplitSourceAnswer(variantId: string): string | undefined {
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0]
  const q = api.demand2.getDemandFraming.useQuery(
    { collectionId: collectionId ?? 0 },
    { enabled: collectionId != null },
  )
  return q.data?.variantAnswers.find(
    (a) => a.question === "sizeSplitSource" && a.variantId === variantId,
  )?.answer
}

/**
 * The resolved `sizePreorderSplit` answer, read off the raw rows: `useFraming` keeps only the engine's
 * per-variant questions, so the variant's own row would be lost.
 */
export function useSizePreorderSplitAnswer(variantId: string): string | undefined {
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0]
  const q = api.demand2.getDemandFraming.useQuery(
    { collectionId: collectionId ?? 0 },
    { enabled: collectionId != null },
  )
  return effectiveAnswerFor(q.data ?? NO_FRAMING_ANSWERS, "sizePreorderSplit", {
    kind: "variant",
    variantId,
    nodes: [],
  }).answer
}

/** Every stored answer on the route's workspace, for surfaces that count across questions. */
export function useAllFramingAnswers(): readonly FramingAnswerRow[] {
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0]
  const q = api.demand2.getDemandFraming.useQuery(
    { collectionId: collectionId ?? 0 },
    { enabled: collectionId != null },
  )
  return q.data?.answers ?? EMPTY_ANSWERS
}

export function useFramingAnswers(question: FramingQuestionId | null): readonly FramingAnswerRow[] {
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0]
  const q = api.demand2.getDemandFraming.useQuery(
    { collectionId: collectionId ?? 0 },
    { enabled: collectionId != null && question != null },
  )
  return useMemo(
    () =>
      question == null
        ? EMPTY_ANSWERS
        : (q.data?.answers ?? EMPTY_ANSWERS).filter((a) => a.question === question),
    [q.data, question],
  )
}

export function useSeasonTrendMode(node: FramingNode = WHOLE_COLLECTION_NODE): SeasonTrendMode {
  const { collectionIds } = useDemand2Scope()
  // One id answers for the route: a group resolves through the same workspace as any member.
  const collectionId = collectionIds[0]
  const q = api.demand2.getDemandFraming.useQuery(
    { collectionId: collectionId ?? 0 },
    { enabled: collectionId != null },
  )
  // A node without its own answer inherits the collection's ("Follows overall").
  return useMemo(
    () =>
      parseSeasonTrendMode(effectiveAnswerAt(q.data?.answers ?? [], "seasonTrend", node).answer),
    [q.data, node],
  )
}

type NodeSalesTrend = {
  levelKey: string
  path: string[]
  label: string
  year1: number
  year2: number
  pct: number | null
  carriedPct: number | null
  appliedPct: number | null
}
const EMPTY_NODES: NodeSalesTrend[] = []
const EMPTY_LEVELS: string[] = []

type ScopedIntentRow = {
  variantId: string
  channel: string
  warehouseId: string
  marketId: string
  updatedAt: Date | string
}

/**
 * Rows at the active editing scope plus the concurrency token this client last read per variant:
 * the newest `updatedAt` among its rows, which the mutation compares against. `null` = no row (one
 * appearing since is still a conflict); `undefined` while loading, never sent as `null` or it
 * manufactures a conflict.
 */
export function useScopedIntentRows<T extends ScopedIntentRow>(rows: T[] | undefined) {
  const scope = scopeToColumns(useDemandScope())
  const { channel, warehouseId, marketId } = scope

  const atScope = useMemo(
    () =>
      (rows ?? []).filter(
        (r) => r.channel === channel && r.warehouseId === warehouseId && r.marketId === marketId,
      ),
    [rows, channel, warehouseId, marketId],
  )

  const tokenByVariant = useMemo(() => {
    const m = new Map<string, Date>()
    for (const r of atScope) {
      const at = new Date(r.updatedAt)
      const cur = m.get(r.variantId)
      if (!cur || at > cur) m.set(r.variantId, at)
    }
    return m
  }, [atScope])

  // As the open edit is checked against, not as a colleague's change refreshed them (see `trackAsOfEdit`).
  const tokensAsOfEdit = useAsOfEditStart(rows !== undefined ? tokenByVariant : undefined)
  const tokenFor = useCallback(
    (variantId: string) => {
      const tokens = tokensAsOfEdit()
      return tokens ? (tokens.get(variantId) ?? null) : undefined
    },
    [tokensAsOfEdit],
  )

  return { scope, atScope, tokenFor }
}

export function useDemandForecasts(): [
  Record<string, DraftForecast>,
  (next: Record<string, DraftForecast>) => void,
] {
  const invalidate = useInvalidateDemandReads("override_set", "override_clear")
  const slicesFor = useTotalsReadbackSlices()
  const { collectionIds } = useDemand2Scope()
  const enabled = collectionIds.length > 0
  const q = useEditStateSlice("overrides", collectionIds, enabled)
  const { mutateAsync: setOverride } = api.demand2.setDemandOverride.useMutation()
  const { mutateAsync: clearOverride } = api.demand2.clearDemandOverride.useMutation()
  // Edits land at the active editing slice; the default all-axes scope is the variant total.
  const { scope, atScope, tokenFor } = useScopedIntentRows(q.data)
  const { channel, warehouseId, marketId } = scope

  const value = useMemo<Record<string, DraftForecast>>(() => {
    const m: Record<string, DraftForecast> = {}
    for (const o of atScope) {
      m[o.variantId] = { total: o.total, sizeSplit: {}, weekly: null, source: "manual" }
    }
    return m
  }, [atScope])

  const setValue = useCallback(
    (next: Record<string, DraftForecast>) => {
      const ops: Promise<{ totals?: TotalsReadback | null } | null>[] = []
      const touched: string[] = []
      for (const [variantId, f] of Object.entries(next)) {
        if (value[variantId]?.total !== f.total) touched.push(variantId)
      }
      for (const variantId of Object.keys(value)) {
        if (!(variantId in next)) touched.push(variantId)
      }
      // Resolved once: every per-variant mutation in the batch answers at the same slices.
      const sent = slicesFor(touched)
      for (const [variantId, f] of Object.entries(next)) {
        if (value[variantId]?.total !== f.total) {
          ops.push(
            setOverride({
              collectionIds,
              variantId,
              total: f.total,
              expectedUpdatedAt: tokenFor(variantId),
              channel,
              warehouseId,
              marketId,
              readback: sent,
            }),
          )
        }
      }
      for (const variantId of Object.keys(value)) {
        if (!(variantId in next)) {
          ops.push(
            clearOverride({
              collectionIds,
              variantId,
              expectedUpdatedAt: tokenFor(variantId),
              channel,
              warehouseId,
              marketId,
              includeNarrower: true,
              readback: sent,
            }),
          )
        }
      }
      // Rejections still refetch, so the planner sees the winning value, not their rejected one.
      void Promise.all(ops)
        .then(
          (results) => invalidate(touched, mergeReadback(sent, results)),
          (err) => {
            showErrorToast(err)
            return invalidate(touched)
          },
        )
        .catch(noop)
    },
    [
      slicesFor,
      value,
      collectionIds,
      setOverride,
      clearOverride,
      invalidate,
      channel,
      warehouseId,
      marketId,
      tokenFor,
    ],
  )

  return [value, setValue]
}

/**
 * Leaf-exact edits to one variant's cube cells: the lock pin and the "Independent" manual-stage
 * delta. Separate from `useDemandForecasts` because the breakdown edits several leaf scopes at
 * once. Owns the overrides read so each write's concurrency token is the one this client read.
 */
export function useDemandCellEdits(
  variantId: string,
  collectionIds: number[],
): {
  overrides: RouterOutputs["demand2"]["getDemandEditState"]["overrides"]
  lock: (cell: ScopeColumns, total: number) => void
  unlock: (cell: ScopeColumns) => void
  setDelta: (cell: ScopeColumns, existingId: string | undefined, value: number) => void
} {
  const invalidate = useInvalidateDemandReads(
    "override_set",
    "override_clear",
    "adjustment_add",
    "adjustment_edit",
    "adjustment_delete",
  )
  const slicesFor = useTotalsReadbackSlices()
  const enabled = variantId !== "" && collectionIds.length > 0
  const q = useEditStateSlice("overrides", collectionIds, enabled)
  const { mutateAsync: setOverride } = api.demand2.setDemandOverride.useMutation()
  const { mutateAsync: clearOverride } = api.demand2.clearDemandOverride.useMutation()
  const { mutateAsync: saveAdjustment } = api.demand2.saveDemandAdjustment.useMutation()
  const { mutateAsync: removeAdjustment } = api.demand2.deleteDemandAdjustment.useMutation()

  const overrides = useMemo(
    () => (q.data ?? []).filter((o) => o.variantId === variantId),
    [q.data, variantId],
  )

  // Per-cell token as in `useScopedIntentRows`: `null` for no pin, `undefined` before load, and
  // as the open edit is checked against.
  const overridesAsOfEdit = useAsOfEditStart(q.data !== undefined ? overrides : undefined)
  const expectedFor = useCallback(
    (cell: ScopeColumns) => {
      const rows = overridesAsOfEdit()
      if (!rows) return undefined
      const row = rows.find(
        (o) =>
          o.channel === cell.channel &&
          o.warehouseId === cell.warehouseId &&
          o.marketId === cell.marketId,
      )
      return row ? new Date(row.updatedAt) : null
    },
    [overridesAsOfEdit],
  )

  // A rejected guard still refetches, so the planner sees the winning value.
  const lock = useCallback(
    (cell: ScopeColumns, total: number) => {
      const sent = slicesFor([variantId])
      void setOverride({
        collectionIds,
        variantId,
        total,
        expectedUpdatedAt: expectedFor(cell),
        ...cell,
        readback: sent,
      })
        .then(
          (r) => invalidate([variantId], readbackOf(sent, r)),
          (err) => {
            showErrorToast(err)
            return invalidate([variantId])
          },
        )
        .catch(noop)
    },
    [collectionIds, variantId, setOverride, expectedFor, invalidate, slicesFor],
  )

  const unlock = useCallback(
    (cell: ScopeColumns) => {
      const sent = slicesFor([variantId])
      void clearOverride({
        collectionIds,
        variantId,
        expectedUpdatedAt: expectedFor(cell),
        ...cell,
        readback: sent,
      })
        .then(
          (r) => invalidate([variantId], readbackOf(sent, r)),
          (err) => {
            showErrorToast(err)
            return invalidate([variantId])
          },
        )
        .catch(noop)
    },
    [collectionIds, variantId, clearOverride, expectedFor, invalidate, slicesFor],
  )

  // Replaces (never stacks) the cell's "Independent" delta as a windowless manual-stage row, so it
  // lands in Manual, never On-top, and moves no other cell's base.
  const setDelta = useCallback(
    (cell: ScopeColumns, existingId: string | undefined, value: number) => {
      // Only the final write reads back; it would overwrite the clear's answer anyway.
      const sent = slicesFor([variantId])
      void Promise.resolve(existingId ? removeAdjustment({ collectionIds, id: existingId }) : null)
        .then(() =>
          value !== 0
            ? saveAdjustment({
                collectionIds,
                variantIds: [variantId],
                ...cell,
                stage: "manual",
                type: "add",
                value,
                name: MANUAL_ADJUSTMENT_NAME,
                theme: CELL_DELTA_THEME,
                readback: sent,
              })
            : null,
        )
        .then(
          (r) => invalidate([variantId], readbackOf(sent, r)),
          // A failed write leaves the old delta already deleted, so refetch.
          (err) => {
            showErrorToast(err)
            return invalidate([variantId])
          },
        )
        .catch(noop)
    },
    [collectionIds, variantId, removeAdjustment, saveAdjustment, invalidate, slicesFor],
  )

  return { overrides, lock, unlock, setDelta }
}
